import { once, onceEach } from "~/jose_modern/once.js";
import { Modal } from "~/jose_modern/modal.js";
import { Flash } from "~/jose_modern/flash.js";
import { requestJson } from "./requests.js";
import { ingredientCustomMeasuresPath } from "routes.js";

const MODAL_SELECTOR = "#custom-measure-modal";
const SYNTHETIC_VALUE = "__add_custom_unit__";

const SELECTORS = {
  modeTab: "[data-cmm-mode-tab]",
  weightUnit: "[data-cmm-weight-unit]",
  volumeUnit: "[data-cmm-volume-unit]",
  volumeUnitWrap: "[data-cmm-volume-unit-wrap]",
  volumeType: "[data-cmm-volume-type]",
  volumeTypeRow: "[data-cmm-volume-type-row]",
  helpWeight: "[data-cmm-help-weight]",
  helpVolume: "[data-cmm-help-volume]",
  tabs: "[data-cmm-tabs]",
  equivalentUnit: "[data-cmm-equivalent-unit]",
  quantity: "[data-cmm-quantity]",
  equivalentQuantity: "[data-cmm-equivalent-quantity]",
  errors: "[data-cmm-errors]",
  save: "[data-cmm-save]",
  trigger: "select[data-cmm-trigger]",
  target: "[data-cmm-target]",
};

let pendingTrigger = null;

const getModal = () => document.querySelector(MODAL_SELECTOR);

const parseUnits = (raw) => {
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

const setMode = (modal, mode) => {
  modal.dataset.cmmMode = mode;
  const isWeight = mode === "weight";

  modal.querySelectorAll(SELECTORS.modeTab).forEach((tab) => {
    tab.classList.toggle("is-active", tab.dataset.cmmModeTab === mode);
  });
  modal.querySelector(SELECTORS.weightUnit).classList.toggle("is-hidden", !isWeight);
  modal.querySelector(SELECTORS.volumeUnitWrap).classList.toggle("is-hidden", isWeight);
  modal.querySelector(SELECTORS.volumeTypeRow).classList.toggle("is-hidden", isWeight);
  modal.querySelector(SELECTORS.helpWeight).classList.toggle("is-hidden", !isWeight);
  modal.querySelector(SELECTORS.helpVolume).classList.toggle("is-hidden", isWeight);
};

const setAllowVolume = (modal, allowVolume) => {
  modal.querySelector(SELECTORS.tabs).classList.toggle("is-hidden", !allowVolume);
};

const setEquivalentUnits = (modal, units) => {
  const select = modal.querySelector(SELECTORS.equivalentUnit);
  const options = units?.length ? units : ["gram"];
  select.innerHTML = "";
  options.forEach((unit) => {
    const opt = document.createElement("option");
    opt.value = unit;
    opt.textContent = unit;
    select.appendChild(opt);
  });
  select.value = options.includes("gram") ? "gram" : options[0];
};

const setTarget = (modal, userName) => {
  const target = modal.querySelector(SELECTORS.target);
  if (!target) return;

  target.textContent = userName ? `${userName}'s` : "your";
};

const resetModal = (modal, { allowVolume, equivalentUnits, userName }) => {
  modal.querySelectorAll("input").forEach((input) => (input.value = ""));
  setEquivalentUnits(modal, equivalentUnits);
  const errors = modal.querySelector(SELECTORS.errors);
  errors.textContent = "";
  errors.classList.add("is-hidden");
  setAllowVolume(modal, allowVolume);
  setMode(modal, "weight");
  setTarget(modal, userName);
};

const buildMeasurePayload = (modal) => {
  const mode = modal.dataset.cmmMode || "weight";
  const base = {
    quantity: Number(modal.querySelector(SELECTORS.quantity).value),
    equivalent_quantity: Number(modal.querySelector(SELECTORS.equivalentQuantity).value),
    equivalent_unit: modal.querySelector(SELECTORS.equivalentUnit).value,
  };

  if (mode === "weight") {
    return {
      ...base,
      unit: modal.querySelector(SELECTORS.weightUnit).value.trim(),
    };
  }

  return {
    ...base,
    unit: modal.querySelector(SELECTORS.volumeUnit).value,
    volume_type: modal.querySelector(SELECTORS.volumeType).value.trim(),
  };
};

const showErrors = (modal, messages) => {
  const errors = modal.querySelector(SELECTORS.errors);
  errors.innerHTML = "";
  messages.forEach((message) => {
    const line = document.createElement("div");
    line.textContent = message;
    errors.appendChild(line);
  });
  errors.classList.remove("is-hidden");
};

const fullUnitFor = (measure) =>
  measure.volume_type ? `${measure.unit}, ${measure.volume_type}` : measure.unit;

const applyResult = ({ measure, valid_units: validUnits }) => {
  const select = pendingTrigger.selectEl;
  const newUnit = fullUnitFor(measure);
  const synthetic = select.querySelector(`option[value="${SYNTHETIC_VALUE}"]`);
  const existing = new Set(Array.from(select.options).map((o) => o.value));

  (validUnits || []).forEach((unit) => {
    if (existing.has(unit)) return;

    const opt = document.createElement("option");
    opt.value = unit;
    opt.textContent = unit;
    if (synthetic) {
      select.insertBefore(opt, synthetic);
    } else {
      select.appendChild(opt);
    }
  });

  select.value = newUnit;
  select.dispatchEvent(new Event("change", { bubbles: true }));
};

const submitMeasure = async (modal) => {
  const saveButton = modal.querySelector(SELECTORS.save);
  if (saveButton.disabled) return;

  modal.querySelector(SELECTORS.errors).classList.add("is-hidden");
  saveButton.disabled = true;

  try {
    const params = { measure: buildMeasurePayload(modal) };
    if (pendingTrigger.userId) params.user_id = pendingTrigger.userId;

    const { response, data } = await requestJson(
      ingredientCustomMeasuresPath({ ingredient_id: pendingTrigger.ingredientId }),
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(params),
      }
    );

    if (!response.ok) {
      showErrors(modal, data?.errors || ["Could not save."]);
      return;
    }

    applyResult(data);
    Modal.hide(MODAL_SELECTOR);
    Flash.success("Custom measure added.");
  } finally {
    saveButton.disabled = false;
  }
};

const openFor = (trigger) => {
  const modal = getModal();
  if (!modal) return;
  pendingTrigger = trigger;
  resetModal(modal, {
    allowVolume: trigger.allowVolume,
    equivalentUnits: trigger.equivalentUnits,
    userName: trigger.userName,
  });
  Modal.show(MODAL_SELECTOR);
};

const wireSelect = (select) => {
  let previousValue = select.value;

  select.addEventListener(
    "change",
    (event) => {
      if (select.value !== SYNTHETIC_VALUE) {
        previousValue = select.value;
        return;
      }
      event.stopPropagation();
      event.preventDefault();
      select.value = previousValue;
      openFor({
        selectEl: select,
        ingredientId: select.dataset.cmmIngredientId,
        userId: select.dataset.cmmUserId,
        userName: select.dataset.cmmUserName,
        allowVolume: select.dataset.cmmAllowVolume === "true",
        equivalentUnits: parseUnits(select.dataset.cmmEquivalentUnits),
      });
    },
    { capture: true }
  );
};

const wireModal = (modal) => {
  modal.querySelectorAll(SELECTORS.modeTab).forEach((tab) => {
    tab.addEventListener("click", (event) => {
      event.preventDefault();
      setMode(modal, tab.dataset.cmmModeTab);
    });
  });

  modal.querySelector(SELECTORS.save).addEventListener("click", (event) => {
    event.preventDefault();
    submitMeasure(modal);
  });
};

const initialize = (container) => {
  onceEach(container.querySelectorAll(SELECTORS.trigger), "custom-measure-trigger", wireSelect);

  const modal = getModal();
  if (modal) once(modal, "custom-measure-modal", wireModal);
};

document.addEventListener("DOMContentLoaded", () => initialize(document));
document.addEventListener("partial:replace", (ev) => initialize(ev.container));
